<template>
  <section class="address-map__wrapper">
    <template v-if="!hasError">
      <l-map :zoom="mapOptions.zoom" :animated="true"
             :center="mapOptions.mapCenter"
             :options="{ detectRetina: true, crossOrigin: true }"
             @click="toggleMarker" @ready="onMapReady" @locationfound="onLocationFound" @locationerror="onLocationError">
        <l-tile-layer :url="mapOptions.tileUrl" :attribution="mapOptions.attribution" />
        <l-marker :lat-lng="mapOptions.markerPosition">
          <l-icon :iconUrl="mapOptions.markerIcon.iconUrl" :shadowUrl="mapOptions.markerIcon.shadowUrl" :iconRetinaUrl="mapOptions.markerIcon.iconRetinaUrl" />
        </l-marker>
      </l-map>
      <div class="geolocation-control">
        <span v-if="locating && !locationInaccurate">
          {{ $t('Locating the most precise address') }}
        </span>
        <span v-else-if="!locatingError && !locationInaccurate">
          {{ $t('Use current location') }}
        </span>
        <span v-else-if="locationInaccurate">
          {{ $t('Location accuracy is too low. Please, try to put marker in the right place manually!') }}
        </span>
        <span v-else>
          {{ $t('Error happened during address locating') }}
        </span>
        <mini-button-full class="address_btn" @click.native="locate">
          {{ locatingError ? $t('Refresh') : $t('Locate') }}
        </mini-button-full>
      </div>
    </template>
    <template v-else>
      <p class="modal-address-map__error">{{ $t('Cannot load the map. Please, contact the support. Error: ', {error: hasError}) }}</p>
    </template>
  </section>
</template>
<script>
import config from 'config';
import 'leaflet/dist/leaflet.css';

const MiniButtonFull = () => import(/* webpackChunkName: "vsf-mini-button-full" */ 'theme/components/theme/MiniButtonFull.vue');
export default {
  name: 'AddressMap',
  components: {
    MiniButtonFull,
    BaseAddressAutocompleteInput: () => import(/* webpackChunkName: "vsf-base-address-autocomplete-input" */ 'theme/components/core/blocks/Form/BaseAddressAutocompleteInput.vue')
  },
  props: {
    currentAddress: {
      type: Object,
      required: false,
      default: null
    }
  },
  data: () => ({
    hasError: false,
    mapOptions: {
      zoom: 24,
      mapCenter: [49.555681281714364, 25.592393875122074], // Ternopil
      markerPosition: [49.555681281714364, 25.592393875122074],
      attribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      markerIcon: {
        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png'
      }
    },
    timeout: null,
    selectedAddressData: {},
    locateAvailable: false,
    locating: false,
    mapInstance: null,
    locatingError: null,
    locationInaccurate: false,
    locationCircle: null
  }),
  methods: {
    onMapReady (map) {
      this.locateAvailable = true;
      this.mapInstance = map;
    },
    onLocationFound (l) {
      this.locatingError = null;
      this.toggleMarker(l);
      if (l.accuracy > 50) {
        this.locationInaccurate = true;
        console.log('Accuracy is too low: ', l.accuracy);
      }
      if (L) {
        this.locationCircle = L.circle(l.latlng, l.accuracy)
        this.locationCircle.addTo(this.mapInstance);
      }
      this.locating = false;
    },
    onLocationError (e) {
      console.error(e);
      this.locatingError = e;
      this.locating = false;
    },
    locate () {
      if (!this.locateAvailable) {
        console.error('Map is not ready yet');
        return;
      }
      this.locationInaccurate = false;
      if (this.locationCircle) {
        this.mapInstance.removeLayer(this.locationCircle);
        this.locationCircle = null;
      }

      this.locating = true;
      this.mapInstance.locate({
        setView: true,
        enableHighAccuracy: true,
        maximumAge: 5000
      });
    },
    async loadMapComponents () {
      try {
        await Promise.all([
          import('vue2-leaflet').then((m) => (this.LMap = m.LMap)),
          import('vue2-leaflet').then((m) => (this.LTileLayer = m.LTileLayer)),
          import('vue2-leaflet').then((m) => (this.LMarker = m.LMarker)),
          import('vue2-leaflet').then((m) => (this.LIcon = m.LIcon))
        ]);
      } catch (error) {
        console.error('Error loading map components:', error);
        this.hasError = error.hasOwnProperty('message') ? error.message : error;
      }
    },
    toggleMarker (event) {
      clearTimeout(this.timeout);

      this.locationInaccurate = false;
      if (this.locationCircle) {
        this.locationCircle.removeFrom(this.mapInstance);
        this.locationCircle = null;
      }

      this.mapOptions.markerPosition = event.latlng;
      this.mapInstance.setView({
        lat: event.latlng.lat,
        lng: event.latlng.lng
      }, this.mapOptions.zoom);
      this.timeout = setTimeout(() => {
        fetch(`https://${config.rad.base_url}/address/suggestion?address=${event.latlng.lat}, ${event.latlng.lng}&snh=0`)
          .then(response => response.json())
          .then(data => {
            data = data.length ? data[0] : null;
            if (!data) {
              return;
            }
            this.$emit('address-selected', data);
            this.$bus.$emit('address-autocomplete-set-search', data.display_name);
          })
          .catch(error => {
            console.error('Error fetching data from Addresses service: ', error)
          })
      }, 500)
    }
  },
  created () {
    this.loadMapComponents();
  },
  beforeDestroy () {
    if (this.timeout) clearTimeout(this.timeout);
  },
  watch: {
    currentAddress (value) {
      if (!value) return;
      this.mapOptions.markerPosition = [value.lat, value.lon];
      this.mapInstance.setView({
        lat: value.lat,
        lng: value.lon
      }, this.mapOptions.zoom);
    }
  }
}
</script>
<style scoped lang="scss">
@import '~theme/css/layout/screen';

.address-map__wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.geolocation-control {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: start;
  margin: 20px 0 0;
  span {
    font-size: 1rem;
    font-weight: 600;
    margin-right: 10px;
    width: 80%;
  }
  button {
    width: auto;
    flex: auto;
    font-size: 1rem;
    font-weight: 600;
    &.error {
      background-color: orangered;
      &:hover {
        background-color: red;
      }
    }
  }

  @media only screen and (max-width: $screen-sm-l) {
    flex-direction: column;
    row-gap: 12px;

    span, button {
      width: 100%;
    }
  }
}
</style>
